import { ReactNode, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { PipelineCostBadge } from '../../credits/PipelineCostBadge';
import type { CostItem } from '../../credits/CreditCostNotice';
import { SparkleIcon, stripOutputContract } from './shared';

// Peças do editor de "nodes guiados" usadas por Fotos e Vídeos E-commerce:
// os nodes ficam em ordem de execução (sem layout livre) e as setas são
// derivadas das referências de cada etapa.

export interface FlowSource { id: string; icon: string; title: string; subtitle: string; dashed?: boolean; borderCls: string; labelCls: string }
export interface FlowEdge {
  from: string; to: string; color: string;
  // above: entra pelo topo (ou reto, se vizinho); below: entra por baixo.
  route: 'above' | 'below';
  dashed?: boolean;
  // Só aparece quando uma das pontas está selecionada (evita poluir o fluxo).
  onlyWhenHot?: boolean;
}
export interface FlowNode {
  id: string; icon: string; kindLabel: string; headCls: string; ringCls: string;
  title: string; snippet: string; approval?: boolean; badges?: ReactNode;
}
export interface InsertOption { key: string; icon: string; label: string; hint?: string }

type Box = { x: number; y: number; w: number; h: number };

export function FlowCanvas({ sources, nodes, edges, legend, selectedId, onSelect, readonly, onMove, insertOptions, onInsert }: {
  sources: FlowSource[]; nodes: FlowNode[]; edges: FlowEdge[];
  legend: { color: string; label: string; dashed?: boolean }[];
  selectedId: string | null; onSelect: (id: string) => void;
  readonly: boolean;
  onMove: (from: number, to: number) => void;
  insertOptions: InsertOption[];
  onInsert: (key: string, position: number) => void;
}) {
  const [insertAt, setInsertAt] = useState<number | null>(null);
  const insertMenuRef = useRef<HTMLDivElement | null>(null);
  // Fecha o menu "+" ao clicar fora dele. Uma camada fixa por cima não serve aqui:
  // cada node cria seu próprio contexto de empilhamento e a camada cobria o menu.
  useEffect(() => {
    if (insertAt === null) return;
    const onDown = (e: MouseEvent) => { if (!insertMenuRef.current?.contains(e.target as Node)) setInsertAt(null); };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setInsertAt(null); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [insertAt]);
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const canvasRef = useRef<HTMLDivElement>(null);
  const nodeRefs = useRef<Record<string, HTMLElement | null>>({});
  const [boxes, setBoxes] = useState<Record<string, Box>>({});
  const [size, setSize] = useState({ w: 0, h: 0 });

  const measure = useCallback(() => {
    const root = canvasRef.current;
    if (!root) return;
    const base = root.getBoundingClientRect();
    const next: Record<string, Box> = {};
    for (const [id, el] of Object.entries(nodeRefs.current)) {
      if (!el) continue;
      const r = el.getBoundingClientRect();
      next[id] = { x: r.left - base.left, y: r.top - base.top, w: r.width, h: r.height };
    }
    setBoxes(next);
    setSize({ w: root.scrollWidth, h: root.scrollHeight });
  }, []);
  const layoutKey = nodes.map(n => n.id).join('|') + '#' + sources.map(s => s.id).join('|');
  useLayoutEffect(() => { measure(); }, [measure, layoutKey, selectedId]);
  useEffect(() => {
    const root = canvasRef.current;
    if (!root || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => measure());
    ro.observe(root);
    return () => ro.disconnect();
  }, [measure]);

  // Selecionar uma etapa fora da área visível (ex.: pelo painel de montagem) rola o canvas até ela.
  useEffect(() => {
    if (selectedId) nodeRefs.current[selectedId]?.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
  }, [selectedId]);

  const nodeIndex = (id: string) => nodes.findIndex(n => n.id === id);
  const isSource = (id: string) => sources.some(s => s.id === id);
  const edgePath = (e: FlowEdge) => {
    const a = boxes[e.from]; const b = boxes[e.to];
    if (!a || !b) return null;
    if (e.route === 'below') {
      const x1 = a.x + a.w; const y1 = a.y + a.h / 2;
      const x2 = b.x + b.w / 2; const y2 = b.y + b.h;
      const dip = Math.max(y1, y2) + 44;
      return `M ${x1} ${y1} C ${x1 + 40} ${dip}, ${x2} ${dip}, ${x2} ${y2}`;
    }
    const fromSource = isSource(e.from);
    const adjacent = fromSource ? nodeIndex(e.to) === 0 : nodeIndex(e.to) - nodeIndex(e.from) === 1;
    if (adjacent) {
      const x1 = a.x + a.w; const y1 = a.y + a.h / 2;
      const x2 = b.x; const y2 = b.y + b.h / 2;
      return `M ${x1} ${y1} C ${x1 + 18} ${y1}, ${x2 - 18} ${y2}, ${x2} ${y2}`;
    }
    // Saltos por cima dos nodes intermediários, mais altos quanto mais longe.
    const dist = fromSource ? nodeIndex(e.to) + 1 : Math.abs(nodeIndex(e.to) - nodeIndex(e.from));
    const lift = Math.min(28 + dist * 12, 84);
    const x1 = fromSource ? a.x + a.w * 0.75 : a.x + a.w / 2; const y1 = a.y;
    const x2 = b.x + b.w / 2; const y2 = b.y;
    return `M ${x1} ${y1} C ${x1} ${y1 - lift}, ${x2} ${y2 - lift}, ${x2} ${y2}`;
  };
  const colors = Array.from(new Set(edges.map(e => e.color)));
  const markerId = (c: string) => `arrow-${c.replace('#', '')}`;

  const renderInserter = (position: number) => readonly ? <div className="w-8 flex-shrink-0" /> : (
    <div ref={insertAt === position ? insertMenuRef : undefined} className="relative flex w-8 flex-shrink-0 items-center justify-center self-stretch">
      <button type="button" onClick={() => setInsertAt(insertAt === position ? null : position)} title="Adicionar etapa aqui"
        className={`z-[2] flex h-6 w-6 items-center justify-center rounded-full border text-sm leading-none shadow-sm transition ${insertAt === position ? 'border-indigo-500 bg-indigo-600 text-white' : 'border-slate-300 bg-white text-slate-500 hover:border-indigo-400 hover:text-indigo-600'}`}>+</button>
      {insertAt === position && (
        <div className="absolute top-1/2 z-30 mt-4 w-60 rounded-xl border border-slate-200 bg-white p-1 shadow-xl">
          {insertOptions.map(o => (
            <button key={o.key} type="button" onClick={() => { setInsertAt(null); onInsert(o.key, position); }}
              className="flex w-full items-start gap-2 rounded-lg px-2 py-1.5 text-left hover:bg-slate-50">
              <span className="text-sm leading-5">{o.icon}</span>
              <span>
                <span className="block text-xs font-semibold text-slate-700">{o.label}</span>
                {o.hint && <span className="block text-[10px] text-slate-400">{o.hint}</span>}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <div className="overflow-hidden rounded-2xl border border-slate-200 bg-slate-50">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-200 bg-white px-4 py-2">
        <p className="text-xs font-semibold text-slate-600">Fluxo de geração <span className="font-normal text-slate-400">— clique numa etapa para editar{readonly ? '' : ', arraste para reordenar'}</span></p>
        <div className="hidden flex-wrap items-center gap-3 text-[10px] text-slate-500 sm:flex">
          {legend.map(l => (
            <span key={l.label} className="flex items-center gap-1">
              <span className="w-4" style={{ borderTop: `2px ${l.dashed ? 'dashed' : 'solid'} ${l.color}` }} />{l.label}
            </span>
          ))}
        </div>
      </div>
      <div className="overflow-x-auto" style={{ backgroundImage: 'radial-gradient(#cbd5e1 1px, transparent 1px)', backgroundSize: '18px 18px' }}>
        <div ref={canvasRef} className="relative inline-flex min-w-full items-center px-5 pb-20 pt-28">
          <svg className="pointer-events-none absolute left-0 top-0" width={size.w} height={size.h} aria-hidden="true">
            <defs>
              {colors.map(c => (
                <marker key={c} id={markerId(c)} viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
                  <path d="M 0 0 L 10 5 L 0 10 z" fill={c} />
                </marker>
              ))}
            </defs>
            {edges.map(e => {
              const hot = !!selectedId && (e.to === selectedId || e.from === selectedId);
              if (e.onlyWhenHot && !hot) return null;
              const d = edgePath(e);
              if (!d) return null;
              return (
                <path key={`${e.from}-${e.to}-${e.route}`} d={d} fill="none" stroke={e.color}
                  strokeWidth={hot ? 2.5 : 1.5} strokeOpacity={selectedId && !hot ? 0.25 : 0.9}
                  strokeDasharray={e.dashed ? '5 4' : undefined} markerEnd={`url(#${markerId(e.color)})`} />
              );
            })}
          </svg>

          {/* Entradas fixas */}
          <div className="relative z-[1] flex flex-shrink-0 flex-col gap-3">
            {sources.map(s => (
              <div key={s.id} ref={el => { nodeRefs.current[s.id] = el; }}
                className={`w-40 rounded-xl border-2 bg-white p-2.5 shadow-sm ${s.dashed ? 'border-dashed' : ''} ${s.borderCls}`}>
                <p className={`text-[10px] font-bold uppercase tracking-wide ${s.labelCls}`}>Entrada</p>
                <p className="text-sm font-bold text-slate-800">{s.icon} {s.title}</p>
                <p className="text-[10px] text-slate-500">{s.subtitle}</p>
              </div>
            ))}
          </div>

          {nodes.map((n, idx) => {
            const isSel = n.id === selectedId;
            return (
              <div key={n.id} className={`relative flex items-center ${insertAt === idx ? 'z-30' : 'z-[1]'}`}>
                {renderInserter(idx)}
                <div
                  ref={el => { nodeRefs.current[n.id] = el; }}
                  role="button" tabIndex={0}
                  draggable={!readonly}
                  onDragStart={() => setDragIndex(idx)}
                  onDragEnd={() => setDragIndex(null)}
                  onDragOver={e => { if (dragIndex !== null) e.preventDefault(); }}
                  onDrop={() => { if (dragIndex !== null && dragIndex !== idx) onMove(dragIndex, idx); setDragIndex(null); }}
                  onClick={() => onSelect(n.id)}
                  onKeyDown={e => { if (e.key === 'Enter') onSelect(n.id); }}
                  className={`flex h-[136px] w-48 flex-shrink-0 flex-col overflow-hidden rounded-xl border bg-white text-left shadow-sm transition ${isSel ? `border-transparent ring-2 ${n.ringCls} shadow-md` : 'border-slate-200 hover:shadow-md'} ${dragIndex === idx ? 'opacity-40' : ''} ${readonly ? 'cursor-pointer' : 'cursor-grab active:cursor-grabbing'}`}
                >
                  <div className={`flex items-center gap-2 px-2.5 py-1.5 text-white ${n.headCls}`}>
                    <span className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded bg-white/25 text-[10px] font-bold">{idx + 1}</span>
                    <span className="truncate text-[10px] font-bold uppercase tracking-wide">{n.icon} {n.kindLabel}</span>
                    {n.approval && <span className="ml-auto flex-shrink-0 rounded bg-white/25 px-1.5 text-[9px] font-bold">APROVAÇÃO</span>}
                  </div>
                  <div className="p-2.5">
                    <p className="line-clamp-2 text-sm font-bold text-slate-800">{n.title}</p>
                    <p className="mt-1 line-clamp-2 text-[11px] text-slate-500">{n.snippet.replace(/```[a-z]*\s*/gi, '').trim() || <i className="text-amber-600">Sem instrução</i>}</p>
                    {n.badges && <div className="mt-2 flex flex-wrap gap-1">{n.badges}</div>}
                  </div>
                </div>
              </div>
            );
          })}
          <div className={`relative flex items-center ${insertAt === nodes.length ? 'z-30' : 'z-[1]'}`}>
            {renderInserter(nodes.length)}
            {!nodes.length && <p className="text-xs text-slate-500">Adicione a primeira etapa no botão +</p>}
          </div>
        </div>
      </div>
    </div>
  );
}

// Cabeçalho do estilo: nome/descrição editáveis inline, custo e ações.
export function FlowHeader({ name, description, onName, onDescription, readonly, dirty, saving, costItems, onSave, onClone, onRemove }: {
  name: string; description: string; onName: (v: string) => void; onDescription: (v: string) => void;
  readonly: boolean; dirty: boolean; saving: boolean; costItems: CostItem[];
  onSave: () => void; onClone: () => void; onRemove: () => void;
}) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
      {readonly && (
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2 rounded-xl border border-indigo-200 bg-indigo-50 px-3 py-2">
          <p className="text-xs text-indigo-900"><b>Modelo do sistema.</b> Você pode explorar as etapas, mas para mudar algo crie a sua versão.</p>
          <button type="button" onClick={onClone} className="rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-indigo-500">Usar como base</button>
        </div>
      )}
      <div className="flex flex-wrap items-start gap-3">
        <div className="min-w-[220px] flex-1 space-y-2">
          <input value={name} onChange={e => onName(e.target.value)} disabled={readonly} aria-label="Nome do estilo"
            className="w-full rounded-lg border border-transparent px-2 py-1 text-lg font-bold text-slate-900 hover:border-slate-200 focus:border-indigo-400 focus:outline-none disabled:bg-transparent disabled:text-slate-900" />
          <input value={description} onChange={e => onDescription(e.target.value)} disabled={readonly}
            placeholder={readonly ? '' : 'Descrição curta (aparece no card do estilo)'} aria-label="Descrição do estilo"
            className="w-full rounded-lg border border-transparent px-2 py-1 text-sm text-slate-600 hover:border-slate-200 focus:border-indigo-400 focus:outline-none disabled:bg-transparent" />
          <PipelineCostBadge items={costItems} />
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {!readonly && dirty && <span className="text-xs font-semibold text-amber-600">● Alterações não salvas</span>}
          {!readonly && <button type="button" onClick={onSave} disabled={saving || !dirty} className="rounded-xl bg-slate-950 px-4 py-2 text-sm font-semibold text-white disabled:opacity-40">{saving ? 'Salvando...' : 'Salvar'}</button>}
          {!readonly && <button type="button" onClick={onClone} className="rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">Duplicar</button>}
          {!readonly && <button type="button" onClick={onRemove} className="rounded-xl border border-rose-200 bg-white px-3 py-2 text-sm font-semibold text-rose-600 hover:bg-rose-50">Excluir</button>}
        </div>
      </div>
    </div>
  );
}

// Cabeçalho + abas do painel da etapa selecionada.
export function InspectorHeader<T extends string>({ index, title, headCls, readonly, canLeft, canRight, onLeft, onRight, onRemove, tabs, tab, onTab }: {
  index: number; title: string; headCls: string; readonly: boolean;
  canLeft: boolean; canRight: boolean; onLeft: () => void; onRight: () => void; onRemove: () => void;
  tabs: { id: T; label: string }[]; tab: T; onTab: (t: T) => void;
}) {
  return (
    <>
      <div className="flex items-center gap-2">
        <span className={`flex h-7 w-7 items-center justify-center rounded-lg text-xs font-bold text-white ${headCls}`}>{index + 1}</span>
        <p className="min-w-0 flex-1 truncate text-sm font-bold text-slate-900">{title}</p>
        {!readonly && (
          <div className="flex items-center gap-0.5">
            <button type="button" onClick={onLeft} disabled={!canLeft} title="Mover para a esquerda" className="rounded px-1.5 py-1 text-slate-500 hover:bg-slate-100 disabled:opacity-30">←</button>
            <button type="button" onClick={onRight} disabled={!canRight} title="Mover para a direita" className="rounded px-1.5 py-1 text-slate-500 hover:bg-slate-100 disabled:opacity-30">→</button>
            <button type="button" onClick={onRemove} title="Remover etapa" className="rounded px-1.5 py-1 text-rose-500 hover:bg-rose-50">🗑</button>
          </div>
        )}
      </div>
      <div className="flex rounded-xl bg-slate-100 p-1">
        {tabs.map(t => (
          <button key={t.id} type="button" onClick={() => onTab(t.id)}
            className={`flex-1 rounded-lg px-2 py-1.5 text-xs font-semibold transition ${tab === t.id ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-800'}`}>{t.label}</button>
        ))}
      </div>
    </>
  );
}

// Aba "Instrução": copiloto inline + texto da instrução sem o contrato JSON.
export function InstructionTab({ stepKey, instruction, onChange, readonly, runCopilot, placeholder, help }: {
  stepKey: string; instruction: string; onChange: (v: string) => void; readonly: boolean;
  runCopilot: (direction: string) => Promise<string>;
  placeholder: string; help: string;
}) {
  const [direction, setDirection] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState('');
  useEffect(() => { setDirection(''); setResult(''); }, [stepKey]);
  const visible = stripOutputContract(instruction);

  const run = async () => {
    setBusy(true);
    try { setResult(stripOutputContract(await runCopilot(direction))); }
    catch (e: any) { toast.error(e.message || 'Erro no copiloto'); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-3">
      {!readonly && (
        <div className="rounded-xl border border-fuchsia-200 bg-fuchsia-50/60 p-3">
          <p className="flex items-center gap-1.5 text-xs font-bold text-fuchsia-800"><SparkleIcon className="h-3.5 w-3.5" /> Escrever com IA</p>
          <textarea value={direction} onChange={e => setDirection(e.target.value)} rows={2} placeholder={placeholder}
            className="mt-2 w-full rounded-lg border border-fuchsia-200 bg-white px-3 py-2 text-xs focus:outline-none focus:ring-2 focus:ring-fuchsia-100" />
          <button type="button" onClick={run} disabled={busy} className="mt-2 rounded-lg bg-fuchsia-600 px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50">
            {busy ? 'Escrevendo…' : result ? '↻ Tentar de novo' : visible ? 'Melhorar instrução' : 'Criar instrução'}
          </button>
          {result && (
            <div className="mt-3">
              <p className="text-[11px] font-semibold text-fuchsia-800">Sugestão (pode editar antes de aplicar)</p>
              <textarea value={result} onChange={e => setResult(e.target.value)} rows={8} className="mt-1 w-full rounded-lg border border-fuchsia-200 bg-white px-3 py-2 text-xs" />
              <div className="mt-2 flex justify-end gap-2">
                <button type="button" onClick={() => setResult('')} className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-600">Descartar</button>
                <button type="button" onClick={() => { onChange(result); setResult(''); toast.success('Instrução aplicada'); }} className="rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white">Aplicar</button>
              </div>
            </div>
          )}
        </div>
      )}
      <label className="block">
        <span className="text-xs font-semibold text-slate-600">Instrução da etapa</span>
        <textarea value={visible} onChange={e => onChange(e.target.value)} disabled={readonly} rows={12}
          className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-xs leading-relaxed focus:border-indigo-400 focus:outline-none focus:ring-2 focus:ring-indigo-100 disabled:bg-slate-50 disabled:text-slate-500" />
      </label>
      <p className="text-[11px] text-slate-400">{help} O formato técnico da resposta é adicionado automaticamente.</p>
    </div>
  );
}

// Opção de rádio grande (usada em Referências).
export function ChoiceCard({ active, title, desc, disabled, onClick }: { active: boolean; title: string; desc?: string; disabled?: boolean; onClick: () => void }) {
  return (
    <button type="button" disabled={disabled} onClick={onClick}
      className={`w-full rounded-xl border-2 p-3 text-left transition disabled:cursor-default ${active ? 'border-indigo-500 bg-indigo-50/60' : 'border-slate-200 hover:border-slate-300'}`}>
      <span className={`block text-sm font-semibold ${active ? 'text-indigo-800' : 'text-slate-800'}`}>{active ? '● ' : '○ '}{title}</span>
      {desc && <span className="mt-0.5 block text-[11px] text-slate-500">{desc}</span>}
    </button>
  );
}

// Checkbox com título e explicação.
export function ToggleCard({ checked, onChange, disabled, title, desc, tone = 'slate' }: {
  checked: boolean; onChange: (v: boolean) => void; disabled?: boolean; title: string; desc: string; tone?: 'slate' | 'fuchsia';
}) {
  return (
    <label className={`flex items-start gap-3 rounded-xl border p-3 ${tone === 'fuchsia' ? 'border-fuchsia-200 bg-fuchsia-50/50' : 'border-slate-200'}`}>
      <input type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)} disabled={disabled} className="mt-0.5 h-4 w-4" />
      <span>
        <span className="block text-sm font-semibold text-slate-800">{title}</span>
        <span className="block text-[11px] text-slate-500">{desc}</span>
      </span>
    </label>
  );
}
